import React, {FC, useEffect, useState} from 'react';
import './CardSimulationCclt.scss';
import CardSimulationBase from '../../CardSimulationBase/CardSimulationBase';
import {ProductsAcronymsEnum} from '../../../../../models/Products/ProductsAcronymsEnum';
import {useSimulation} from '../../../../../providers/Typing/SimulationProvider';
import {useSimulationCclt} from '../../../../../providers/Typing/SimulationCcltProvider';
import FormataMoeda from '../../../../../utils/FormataMoeda';
import {Collapse} from 'react-bootstrap';
import FormataData from '../../../../../utils/FormataData';
import FilterSimulationCclt from '../FilterSimulationCclt/FilterSimulationCclt';

interface CardSimulationCcltProps {
    data: any;
    financial: any;
    message: string;
    code: number;
    errors?: string[];
    handleRemove: () => void;
    type: 'first' | 'filter' | 'preview';
    index: number;
    filterActive?: boolean;
    isFilter?: boolean;
    isView?: boolean;
}

const CardSimulationCclt: FC<CardSimulationCcltProps> = (
    {
        data,
        financial,
        message,
        code,
        errors,
        handleRemove,
        type,
        index,
        filterActive = false,
        isFilter = false,
        isView = false,
    }) => {
    const {handleToHire, simulatesSelected} = useSimulation();
    const {retrySimulate} = useSimulationCclt();
    const [proposalCard, setProposalCard] = useState<any>({});
    const [proposalSelected, setProposalSelected] = useState<any>({});
    const [open, setOpen] = useState<boolean>(false);
    const [isRetrying, setIsRetrying] = useState<boolean>(false);

    useEffect(() => {
        if (data) {
            setProposalCard(data);
        }
    }, [data]);

    useEffect(() => {
        for (const key in simulatesSelected) {
            if (key === ProductsAcronymsEnum.CCLT) {
                setProposalSelected(simulatesSelected[key]);
            }
        }
    }, [simulatesSelected]);

    const toggle = () => setOpen((current) => !current);

    const handle = () => {
        handleToHire(ProductsAcronymsEnum.CCLT, {
            produto: ProductsAcronymsEnum.CCLT,
            financeira: financial?.sigla?.toLowerCase(),
            simulacaoId: proposalCard?.simulationId || 0,
            financedAmount: proposalCard?.financedAmount,
            installments: proposalCard?.installments,
        });
    };

    return (
        <div className="CardSimulationCclt" data-testid="CardSimulationCclt">
            <CardSimulationBase
                financial={financial}
                finalizedProposal={proposalCard?.propostaFinalizada ?? true}
                isFilter={isFilter}
                isView={isView}
                isChecked={
                    proposalSelected?.financeira?.toLowerCase() === financial?.sigla?.toLowerCase() &&
                    proposalSelected?.installments === proposalCard?.installments &&
                    proposalSelected?.financedAmount === proposalCard?.financedAmount
                }
                message={message}
                code={code}
                errors={errors}
                handleTryAgain={() =>
                    retrySimulate(
                        financial?.sigla ?? proposalCard?._financeira,
                        proposalCard?.financedAmount,
                        proposalCard?.installments,
                        type,
                        index
                    )
                }
                product={ProductsAcronymsEnum.CCLT}
                handleRemoveAction={handleRemove}
                handleSelectedAction={handle}
                propostaId={data?.propostaId ?? 0}
                isRetrying={isRetrying}
                setIsRetrying={setIsRetrying}
            >
                {data?.isSending && (
                    <div className="is-sending">
                        <p>Enviando a proposta para o banco</p>
                    </div>
                )}

                {type === 'preview' && (
                    <div className="row filter mb-4 py-2 text-end">
                        <div className="col-12">
                            <FilterSimulationCclt type={type} setIsRetrying={setIsRetrying}/>
                        </div>
                    </div>
                )}

                <div className="row align-items-center">
                    <div className="col">
                        <div className="titleDest">Valor simulado</div>
                        <div className="fieldValueDest">
                            {proposalCard && FormataMoeda(proposalCard?.financedAmount)}
                        </div>
                    </div>

                    <div className="col">
                        <p className="title">
                            Qtd.
                            <br/>
                            Parcelas
                        </p>
                        <div
                            className="fieldValue">{proposalCard?.installments ?? proposalCard?.limites?.parcelas ?? ''}</div>
                    </div>

                    <div className="col">
                        <p className="title">
                            Valor
                            <br/>
                            Parcela
                        </p>
                        <div className="fieldValue">
                            {proposalCard && FormataMoeda(proposalCard?.releasedAmount)}
                        </div>
                    </div>

                    {isView && (
                        <div className="col">
                            <p className="title">
                                Valor
                                <br/>
                                IOF
                            </p>
                            <div
                                className="fieldValue">{!!proposalCard?.valorIOF ? FormataMoeda(proposalCard?.valorIOF) : '--'}</div>
                        </div>
                    )}

                    <div className="col">
                        <p className="title">
                            Taxa de
                            <br/>
                            juros
                        </p>
                        <div className="fieldValue">{proposalCard?.clientRate}%</div>
                    </div>

                    <div className="col text-center">
                        {!isView && (code === 200 || code === 201) && !data?.propostaId && (
                            <div className="linhaBotao mb-1">
                                <button type="button" className="btn btn-primary" onClick={handle}>
                                    SELECIONAR
                                </button>
                            </div>
                        )}
                        <a onClick={toggle}>{open ? 'Ver menos' : 'Ver mais'}</a>
                    </div>
                </div>

                <div className="row justify-content-center">
                    <div className="col-12">
                        <Collapse in={open}>
                            <div className="row align-items-end">
                                <div className="col mt-4">
                                    <p className="title">Valor a ser financiado</p>
                                    <div className="fieldValue">{FormataMoeda(proposalCard?.grossAmount)}</div>
                                </div>

                                <div className="col mt-4">
                                    <p className="title">1ª Parcela em</p>
                                    <div className="fieldValue">
                                        {proposalCard && FormataData(proposalCard?.firstDueDate)}
                                    </div>
                                </div>

                                {!isView && (
                                    <div className="col mt-4">
                                        <p className="title">
                                            Valor
                                            <br/>
                                            IOF
                                        </p>
                                        <div className="fieldValue">{FormataMoeda(proposalCard?.IOCAmount)}</div>
                                    </div>
                                )}

                                <div className="col mt-4">
                                    <p className="title">
                                        CET
                                        <br/>
                                        Mensal
                                    </p>
                                    <div className="fieldValue">{proposalCard?.cetMonthlyRate}%</div>
                                </div>

                                <div className="col mt-4">
                                    <p className="title">
                                        CET
                                        <br/>
                                        Anual
                                    </p>
                                    <div className="fieldValue">{proposalCard?.clientAnualRate}%</div>
                                </div>

                                <div className="col mt-4">
                                    <p className="title">
                                        Taxa
                                        <br/>
                                        Anual
                                    </p>
                                    <div className="fieldValue">{proposalCard?.cetRate}%</div>
                                </div>

                                {!filterActive && <div className="col"/>}
                            </div>
                        </Collapse>
                    </div>
                </div>
            </CardSimulationBase>
        </div>
    );
};

export default CardSimulationCclt;

